import { FooterData } from '$core/models/static';
import { LanguageService } from '$core/services/language.service';
import { FooterService, StaticStorage } from '$core/services/static';
import {
  createFailureState,
  createIdleState,
  createLoadingState,
  createSuccessState,
  RequestState,
} from '$core/util';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { startWith, Subscription } from 'rxjs';

@Component({
  selector: 'app-footer',
  templateUrl: './footer.component.html',
  styleUrls: ['./footer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FooterComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly staticStorage = inject(StaticStorage);
  private readonly footerService = inject(FooterService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly staticRequestState =
    signal<RequestState<FooterData>>(createIdleState());

  private loadSubscription?: Subscription;

  protected readonly hasCachedStatic = computed<boolean>(() => {
    const data = this.staticStorage.static();

    const hasFooter = data.footer;
    const hasFooterSlider = data.footer_slider;

    return !!(hasFooter && hasFooterSlider);
  });

  public get lang() {
    return this.languageService.currentLang;
  }

  public ngOnInit(): void {
    this.staticStorage.langChange$
      .pipe(startWith(null), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (!this.staticStorage.hasActual('footer', 'footer_slider')) {
          this.loadData();
        }
      });
  }

  protected reload(): void {
    this.loadData();
  }

  private loadData(): void {
    this.loadSubscription?.unsubscribe();
    this.staticRequestState.set(createLoadingState());
    this.loadSubscription = this.footerService
      .getFooterStatics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: FooterData) =>
          this.staticRequestState.set(createSuccessState(data)),
        error: (error) =>
          this.staticRequestState.set(createFailureState(error)),
      });
  }
}
